"use client";

import { useMemo } from "react";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

import type { QRProps } from "@/contexts/QREditorContext/types";
import { PAGES } from "@/internals/constants/pages";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import { buildGoogleMapsAddressURL } from "@/utils/googleMaps";
import type { UseCouponDataResult } from "../types";
import { getCouponDateStatus } from "../utils";

/**
 * Resolves coupon preview data from props with QREditorContext fallback,
 * address formatting, and coupon date validation status.
 */
export const useCouponData = (props: QRProps<"coupon">): UseCouponDataResult => {
  const { t } = useTranslation(PAGES.QR_COUPON);
  const { data } = usePreviewData(props);
  const isInitialPreview = props?.initPreview;

  const { fullAddress, addressMapsURL, dateStatus, hasLogo, buttonColor, backgroundColor } =
    useMemo(() => {
      if (!data) {
        return {
          fullAddress: "",
          addressMapsURL: buildGoogleMapsAddressURL(""),
          dateStatus: null,
          hasLogo: false,
          buttonColor: undefined,
          backgroundColor: undefined,
        };
      }

      const {
        locationAddress,
        locationNumeration,
        locationCity,
        locationPostalCode,
        locationState,
        locationCountry,
        themeColor,
        logoUrl,
        couponDateTime,
      } = data;

      const resolvedFullAddress =
        isInitialPreview !== true
          ? getFullAddress(
              locationAddress,
              locationNumeration,
              locationCity,
              locationPostalCode,
              locationState,
              locationCountry
            )
          : `${locationAddress}, ${locationPostalCode}, ${locationCity}, ${locationCountry}`;

      const trimmedFullAddress = (resolvedFullAddress ?? "").trim();

      return {
        fullAddress: trimmedFullAddress,
        addressMapsURL: buildGoogleMapsAddressURL(trimmedFullAddress),
        dateStatus: getCouponDateStatus(couponDateTime),
        hasLogo: !isEmpty(logoUrl?.[0]?.url),
        buttonColor: themeColor?.buttonColor,
        backgroundColor: themeColor?.backgroundColor,
      };
    }, [data, isInitialPreview]);

  return {
    t,
    isInitialPreview,
    data,
    addressData: {
      fullAddress,
      addressMapsURL,
    },
    dateStatus,
    hasLogo,
    buttonColor,
    backgroundColor,
  };
};
